import * as React from "react";
import { Box, IconButton, Link, Typography } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { ImagesContext } from "../../Context";
import CustomChip from "../CustomChip";
import PatientEdit from "./PatientEdit";
import PatientInfo from "./PatientInfo";
import { PatientType } from "../types";

interface Props {
  patient: any;
  handlePatientSave: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  handlePatientChange: (updated: Partial<PatientType>) => void;
  setErrorStatus: (hasErrors: boolean) => void;
  claimStatus: string;
  isEditing: boolean;
  setIsEditing: any;
}

const PatientInformation: React.FC<Props> = ({
  patient,
  handlePatientSave,
  handlePatientChange,
  setErrorStatus,
  claimStatus,
  isEditing,
  setIsEditing,
}) => {
  const images = React.useContext(ImagesContext);

  const [expanded, setExpanded] = React.useState(false);

  const requiredFields = [
    "last_name",
    "first_name",
    "address_line_1",
    "city",
    "state",
    "zip",
    "date_of_birth",
    "gender",
    "mobile_phone_number"
  ];

  const missingFields = requiredFields.filter((field) => !patient?.[field]);
  const hasErrors = missingFields.length > 0;

  const formatLabel = (field: string) => {
    const mapping: Record<string, string> = {
      last_name: "Last Name",
      first_name: "First Name",
      address: "Address Line One",
      city: "City",
      state: "State",
      zip: "Zip Code",
      date_of_birth: "Date of Birth",
      gender: "Gender",
      mobile_phone_number: "Phone Number"
    };

    return mapping[field] || field;
  };

  React.useEffect(() => {
    setErrorStatus(!hasErrors);
  }, [hasErrors, setErrorStatus]);

  const handleExpand = () => {
    setExpanded(!expanded);
  };

  const handleSetEditing = () => {
    setIsEditing(true);
  };

  const handleCancelButton = (event) => {
    event.preventDefault();
    setIsEditing(false);
  };

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: "4px",
      }}
    >
      <Box
        className="claim-step-two-list-tile"
        sx={{
          boxShadow: expanded && "0px 7px 7px 0px #00000040",
        }}
      >
        <Box
          sx={{
            display: "flex",
            columnGap: "16px",
            alignItems: "center",
          }}
        >
          <div className="item-icon-container">
            <img
              src={hasErrors ? images.close_icon : images.check_mark_icon}
              alt="Insurance Icon"
              className="action-icon-image"
              style={{ width: 23, maxHeight: 23 }}
            />
          </div>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              rowGap: "10px",
            }}
          >
            <Box display="flex" alignItems="center" columnGap="16px">
              <Typography
                sx={{
                  fontSize: "18px",
                  lineHeight: "18px",
                  fontWeight: 700,
                  letterSpacing: "1px",
                  color: "#4A4442",
                }}
              >
                Patient
              </Typography>
              <Box display="flex" gap="16px" flexWrap="wrap">
                {hasErrors ? (
                  missingFields.map((field) => (
                    <CustomChip
                      key={field}
                      label={`Missing ${formatLabel(field)}`}
                      type="error"
                    />
                  ))
                ) : (
                  <CustomChip label="No Errors" />
                )}
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: "14px",
                lineHeight: "18px",
                fontWeight: 500,
                letterSpacing: "1px",
                color: "#707070",
              }}
            >
              {patient?.last_name}, {patient?.first_name} • MRN:{" "}
              {patient?.mrn_number}
            </Typography>
          </Box>
        </Box>

        {isEditing ? (
          <Box className="row">
            <Typography
              sx={{
                width: "230px",
                fontSize: "12px",
                lineHeight: "16px",
                color: "#707070",
              }}
            >
              NOTE: Changes made here will update the patient’s information
              directly.
            </Typography>
            <Link
              style={{
                marginRight: "16px",
                marginLeft: "32px",
                color: "black",
              }}
              onClick={handleCancelButton}
            >
              Cancel
            </Link>
            <Link className="save-btn" onClick={handlePatientSave}>
              Save
            </Link>
          </Box>
        ) : (
          <IconButton onClick={handleExpand}>
            {expanded ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          </IconButton>
        )}
      </Box>

      {expanded && (
        <>
          {isEditing ? (
            <PatientEdit
              patient={patient}
              handlePatientChange={handlePatientChange}
            />
          ) : (
            <PatientInfo
              patient={patient}
              handleSetEditing={handleSetEditing}
              claimStatus={claimStatus}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default PatientInformation;
